// src/types/admin/PractitionerColorTypes.ts

import axios from "axios";
import type { UserUuid } from "../identity";

// The React Query key for the palette (used by PractitionerColorPicker's own useQuery and
// by both host screens to invalidate it after a 422 colour conflict). Kept here rather than
// on the component file so a host can import it without pulling in the component's own
// network dependency (teamApi.ts -> runtimeEnv.ts) — see the note in components/shared/index.ts.
export const PRACTITIONER_COLOR_PALETTE_QUERY_KEY = ["practitioner-color-palette"];

export interface PractitionerPaletteHolder {
	user_id: UserUuid;
	name: string;
}

export interface PractitionerPaletteEntry {
	key: string;
	name: string;
	hex: string;
	in_use_by: PractitionerPaletteHolder | null;
}

export interface PractitionerColorPaletteResponse {
	palette: PractitionerPaletteEntry[];
}

/**
 * True when `error` is the 422 auth's PUT /admin/staff/{id} (S2) returns when the submitted
 * `team_member_info.color` collides with another practitioner's stored colour — the
 * concurrency case where the palette snapshot the picker loaded is already stale.
 *
 * Detected by the VALIDATION ERROR KEY, not by message text — Laravel's default `unique`
 * message wording is not a contract, and a key-based check keeps working if it changes.
 */
export function isPractitionerColorConflictError(error: unknown): boolean {
	if (!axios.isAxiosError(error) || error.response?.status !== 422) return false;
	const errors = error.response?.data?.errors as Record<string, unknown> | undefined;
	if (!errors) return false;
	return Object.keys(errors).some((key) => key.toLowerCase().includes("color"));
}
